import { Injectable, computed, signal, inject } from '@angular/core';
import { User } from '../models/user.model';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class UserStore {
  private router = inject(Router);

  // Token storage via signal
  private readonly TOKEN_KEY = 'auth_token';
  private readonly USER_KEY = 'user_data';

  private tokenSignal = signal<string | null>(null);
  private userSignal = signal<User | null>(null);

  readonly token = this.tokenSignal.asReadonly();
  readonly user = this.userSignal.asReadonly();
  readonly isAuthenticated = computed(() => !!this.token());

  readonly userId = computed(() => this.user()?._id ?? undefined);
  readonly userEmail = computed(() => this.user()?.contactEmail ?? null);
  readonly username = computed(() => this.user()?.username ?? null);
  readonly userRole = computed(() => this.user()?.role ?? null);

  constructor() {
    this.initializeFromStorage();
  }

  private initializeFromStorage(): void {
    const storedToken = localStorage.getItem(this.TOKEN_KEY);
    if (storedToken) {
      this.tokenSignal.set(storedToken);
    } else {
      // Anonymous first load (e.g. landing, /auth/google/callback?token=…) — do not redirect here;
      // route guards and logout still control access.
      this.tokenSignal.set(null);
      this.userSignal.set(null);
      localStorage.removeItem(this.USER_KEY);
      return;
    }

    const storedUser = localStorage.getItem(this.USER_KEY);
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        this.userSignal.set(user);
      } catch (error) {
        console.error('Error parsing stored user data:', error);
        localStorage.removeItem(this.USER_KEY);
      }
    } else {
      localStorage.removeItem(this.USER_KEY);
    }
  }

  setToken(token: string): void {
    this.tokenSignal.set(token);
    localStorage.setItem(this.TOKEN_KEY, token);
  }

  setUser(user: User): void {
    this.userSignal.set(user);
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
  }

  updateUser(updates: Partial<User>): void {
    const currentUser = this.user();
    if (currentUser) {
      const updatedUser = { ...currentUser, ...updates };
      this.setUser(updatedUser);
    }
  }

  hasRole(role: string): boolean {
    return this.userRole() === role;
  }

  isAdmin(): boolean {
    const role = this.userRole()?.toUpperCase();
    return role === 'ADMIN' || role === 'SUPER-ADMIN' || role === 'SUPER ADMIN';
  }

  isNormalUser(): boolean {
    const role = this.userRole()?.toUpperCase();
    return role === 'NORMAL' || role === 'USER';
  }

  isConfirmed(): boolean {
    return this.user()?.emailConfirmed ?? false;
  }

  clear() {
    this.tokenSignal.set(null);
    this.userSignal.set(null);
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    this.router.navigate(['/auth/login']);
  }
}
